import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';

import * as userApis from 'apis/users';
import * as subscriptionApis from 'apis/subscriptions';
import './MyAccount.scss';

/*
  Account page, converted from the live-test.html mockup.

  GET /v1/users/account only returns firstName, lastName, email, phone and
  createdAt, so everything on the page is built from those five fields -
  the avatar is drawn from the initials rather than an upload, and the
  membership pill comes from the subscription endpoint the Subscription
  page already uses.
*/

const MIN_PASSWORD_LENGTH = 8;

const EMPTY_PASSWORDS = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const toDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatJoinDate = (date) =>
  date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// "2 years with The Taurian" under the join date. Anything under a month
// is not worth a line of its own, so it is left off.
const formatMembershipLength = (date) => {
  const months = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 30.44));
  if (months < 1) return '';

  const years = Math.floor(months / 12);
  const unit = years >= 1
    ? `${years} year${years > 1 ? 's' : ''}`
    : `${months} month${months > 1 ? 's' : ''}`;

  return `${unit} with The Taurian.`;
};

const getInitials = (firstName, lastName) =>
  [firstName, lastName]
    .map((name) => name?.trim()?.[0] || '')
    .join('')
    .toUpperCase();

function MyAccount() {
  const [account, setAccount] = useState({});
  const [subscription, setSubscription] = useState(null);

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
  const [visibleFields, setVisibleFields] = useState([]);
  const [passwordError, setPasswordError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await userApis.fetchAccountDetails();
        setAccount(response.data);
      } catch (error) {
        console.log(error);
      }
    };

    fetchData();
  }, []);

  // Only drives the membership pill, so a member without a subscription is
  // an expected outcome rather than an error worth surfacing.
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await subscriptionApis.fetchSubscription();
        setSubscription(response.data);
      } catch (error) {
        setSubscription(null);
      }
    };

    fetchSubscription();
  }, []);

  const { currentPassword, newPassword, confirmPassword } = passwords;

  const updatePassword = (field) => (event) => {
    setPasswords((previous) => ({ ...previous, [field]: event.target.value }));
    setPasswordError('');
  };

  const toggleVisibility = (field) => () => {
    setVisibleFields((previous) => previous.includes(field)
      ? previous.filter((name) => name !== field)
      : [...previous, field]);
  };

  const closePasswordForm = () => {
    setPasswords(EMPTY_PASSWORDS);
    setVisibleFields([]);
    setPasswordError('');
    setShowPasswordForm(false);
  };

  const handleUpdatePassword = async (event) => {
    event.preventDefault();
    if (saving) return;

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New Password and Confirm Password do not match.');
      return;
    }

    if (newPassword === currentPassword) {
      setPasswordError('New Password should not be same as Current Password.');
      return;
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setPasswordError('');
    setSaving(true);

    try {
      await userApis.changePassword({ currentPassword, newPassword });
      closePasswordForm();
      toast.success('Password Changed Successfully!');
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          setPasswordError('Current password is incorrect.');
        } else if (error.response?.status === 400) {
          // Nest's ValidationPipe sends an array of messages, the handler's
          // own BadRequestException sends a single string.
          const message = error.response.data?.message;
          setPasswordError(
            (Array.isArray(message) ? message.join(' ') : message) || 'Validation failed.'
          );
        } else {
          setPasswordError('Something went wrong. Please try again.');
        }
      } else {
        setPasswordError('An unexpected error occurred.');
      }
    } finally {
      setSaving(false);
    }
  };

  const fullName = [account?.firstName, account?.lastName].filter(Boolean).join(' ');
  const initials = getInitials(account?.firstName, account?.lastName);
  const joinDate = toDate(account?.createdAt);
  const membershipLength = joinDate ? formatMembershipLength(joinDate) : '';
  const isSubscriber = subscription?.status === 'active';

  // autoComplete lets a password manager fill the current password and
  // offer to save the new one.
  const passwordFields = [
    {
      name: 'currentPassword',
      label: 'Current password',
      placeholder: 'Enter your current password',
      autoComplete: 'current-password',
    },
    {
      name: 'newPassword',
      label: 'New password',
      placeholder: `At least ${MIN_PASSWORD_LENGTH} characters`,
      autoComplete: 'new-password',
    },
    {
      name: 'confirmPassword',
      label: 'Confirm new password',
      placeholder: 'Re-enter the new password',
      autoComplete: 'new-password',
    },
  ];

  const requirements = [
    {
      id: 'length',
      label: `At least ${MIN_PASSWORD_LENGTH} characters`,
      met: newPassword.length >= MIN_PASSWORD_LENGTH,
    },
    {
      id: 'match',
      label: 'Both new password fields match',
      met: newPassword.length > 0 && newPassword === confirmPassword,
    },
    {
      id: 'different',
      label: 'Different from your current password',
      met: newPassword.length > 0 && newPassword !== currentPassword,
    },
  ];

  return (
    <div className="acc-page">
      <div className="acc-container">

        <div className="acc-head">
          <span className="acc-eyebrow">My Account</span>
          <h1 className="acc-title">Account Details</h1>
          <p className="acc-sub">Your membership profile and sign-in security, all in one place.</p>
        </div>

        <div className="acc-section">
          <div className="acc-card acc-profile">

            <div className="acc-avatar">
              {initials || <i className="fa-solid fa-user"></i>}
            </div>

            <div className="acc-profile-info">
              <h2 className="acc-name">{fullName || 'Your account'}</h2>
              <div className="acc-meta">
                {account?.email && <span><i className="fa-solid fa-envelope"></i>{account.email}</span>}
                {account?.phone && <span><i className="fa-solid fa-phone"></i>{account.phone}</span>}
              </div>
            </div>

            <div className="acc-profile-side">
              <span className={`acc-pill${isSubscriber ? ' is-active' : ''}`}>
                <i className="fa-solid fa-circle"></i>
                {isSubscriber ? 'Active member' : 'No active plan'}
              </span>
              <Link className="acc-pill-link" to="/dashboard/my-subscription">
                {isSubscriber ? 'Manage subscription' : 'View plans'} <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>

          </div>
        </div>

        <div className="acc-section">
          <div className="acc-section-title">
            <i className="fa-solid fa-id-card"></i>Personal information
          </div>

          <div className="acc-grid">

            <div className="acc-card acc-detail">
              <div className="acc-detail-label"><i className="fa-solid fa-user"></i>Full name</div>
              <div className="acc-detail-value">{fullName || '-'}</div>
            </div>

            <div className="acc-card acc-detail">
              <div className="acc-detail-label"><i className="fa-solid fa-envelope"></i>Email address</div>
              <div className="acc-detail-value">{account?.email || '-'}</div>
              <div className="acc-detail-note">Used to sign in and receive event updates.</div>
            </div>

            <div className="acc-card acc-detail">
              <div className="acc-detail-label"><i className="fa-solid fa-phone"></i>Phone number</div>
              <div className="acc-detail-value">{account?.phone || '-'}</div>
            </div>

            <div className="acc-card acc-detail">
              <div className="acc-detail-label"><i className="fa-solid fa-calendar-check"></i>Member since</div>
              <div className="acc-detail-value">{joinDate ? formatJoinDate(joinDate) : '-'}</div>
              {membershipLength && <div className="acc-detail-note">{membershipLength}</div>}
            </div>

          </div>
        </div>

        <div className="acc-section">
          <div className="acc-section-title">
            <i className="fa-solid fa-shield-halved"></i>Security
          </div>

          <div className={`acc-card acc-panel${showPasswordForm ? ' is-open' : ''}`}>

            <button
              type="button"
              className="acc-panel-head"
              aria-expanded={showPasswordForm}
              onClick={() => (showPasswordForm ? closePasswordForm() : setShowPasswordForm(true))}
            >
              <span className="acc-panel-icon"><i className="fa-solid fa-lock"></i></span>
              <span>
                <span className="acc-panel-title">Change password</span>
                <span className="acc-panel-desc">Use a password you don't use anywhere else.</span>
              </span>
              <i className="fa-solid fa-chevron-down acc-panel-chevron"></i>
            </button>

            {showPasswordForm && (
              <form className="acc-panel-body" onSubmit={handleUpdatePassword}>

                {passwordFields.map(({ name, label, placeholder, autoComplete }) => {
                  const visible = visibleFields.includes(name);

                  return (
                    <div className="acc-field" key={name}>
                      <label htmlFor={name}>{label}</label>
                      <div className="acc-input-wrap">
                        <input
                          id={name}
                          type={visible ? 'text' : 'password'}
                          placeholder={placeholder}
                          autoComplete={autoComplete}
                          value={passwords[name]}
                          onChange={updatePassword(name)}
                        />
                        <button
                          type="button"
                          className="acc-eye"
                          aria-label={visible ? 'Hide password' : 'Show password'}
                          onClick={toggleVisibility(name)}
                        >
                          <i className={`fa-solid ${visible ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="acc-reqs">
                  {requirements.map(({ id, label, met }) => (
                    <div className={`acc-req${met ? ' is-ok' : ''}`} key={id}>
                      <i className={met ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'}></i>
                      {label}
                    </div>
                  ))}
                </div>

                {passwordError && (
                  <div className="acc-error" role="alert">
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <span>{passwordError}</span>
                  </div>
                )}

                <div className="acc-actions">
                  <button
                    type="submit"
                    className="acc-btn acc-btn-primary"
                    disabled={saving}
                  >
                    <i className="fa-solid fa-check"></i>
                    {saving ? 'Updating...' : 'Update password'}
                  </button>
                  <button
                    type="button"
                    className="acc-btn acc-btn-ghost"
                    onClick={closePasswordForm}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>

              </form>
            )}

          </div>
        </div>

      </div>
    </div>
  );
}

export default MyAccount;
